"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useState } from "react";
import { AuthNavbar } from "@/components/auth-navbar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth-client";

function DeviceContent() {
	const router = useRouter();
	const searchParams = useSearchParams();
	const [userCode, setUserCode] = useState(searchParams.get("user_code") || "");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	// Format user code: remove dashes, uppercase, max 8 chars
	const formatCode = (value: string) => {
		return value.replace(/-/g, "").toUpperCase().slice(0, 8);
	};

	// Display formatted code with dash in middle (XXXX-XXXX)
	const displayCode = (code: string) => {
		if (code.length <= 4) {
			return code;
		}
		return `${code.slice(0, 4)}-${code.slice(4)}`;
	};

	const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const formatted = formatCode(e.target.value);
		setUserCode(formatted);
		setError("");
	};

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();

			if (userCode.length !== 8) {
				setError("Please enter the full 8-character code");
				return;
			}

			setLoading(true);
			setError("");

			try {
				// Verify the code exists and is valid
				const response = await authClient.device({
					query: { user_code: userCode },
				});

				if (response.error) {
					if (response.error.status === 404) {
						setError("Invalid code. Please check and try again.");
					} else if (response.error.status === 410) {
						setError(
							"This code has expired. Please request a new one from your device."
						);
					} else {
						setError("Failed to verify code. Please try again.");
					}
					return;
				}

				// Code is valid, redirect to approval page
				router.push(`/device/approve?user_code=${userCode}`);
			} catch {
				setError("Failed to verify code. Please try again.");
			} finally {
				setLoading(false);
			}
		},
		[userCode, router]
	);

	return (
		<div className="flex min-h-svh flex-col">
			<AuthNavbar />

			<div className="flex flex-1 items-center justify-center p-6 md:p-10">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<CardTitle className="text-2xl">Device Authorization</CardTitle>
						<CardDescription>
							Enter the code shown on your device to authorize it
						</CardDescription>
					</CardHeader>
					<CardContent>
						<form className="flex flex-col gap-6" onSubmit={handleSubmit}>
							{error && (
								<Alert variant="destructive">
									<AlertDescription>{error}</AlertDescription>
								</Alert>
							)}

							<div className="grid gap-2">
								<Label htmlFor="user-code">Device Code</Label>
								<Input
									autoComplete="off"
									autoFocus
									className="text-center font-mono text-2xl tracking-widest"
									disabled={loading}
									id="user-code"
									maxLength={9} // 8 chars + 1 dash
									onChange={handleCodeChange}
									placeholder="XXXX-XXXX"
									type="text"
									value={displayCode(userCode)}
								/>
								<p className="text-center text-muted-foreground text-xs">
									Enter the 8-character code displayed on your device
								</p>
							</div>

							<Button
								className="w-full"
								disabled={loading || userCode.length !== 8}
								type="submit"
							>
								{loading ? "Verifying..." : "Continue"}
							</Button>

							<p className="text-center text-muted-foreground text-sm">
								Make sure this is a device you trust. If you didn't request
								this, close this page.
							</p>
						</form>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

export default function DevicePage() {
	return (
		<Suspense
			fallback={
				<div className="flex min-h-screen items-center justify-center">
					Loading...
				</div>
			}
		>
			<DeviceContent />
		</Suspense>
	);
}
